import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getImageAlt } from "../lib/imageMeta";
import { applyPageMeta, PAGE_SEO_CONFIG } from "../lib/seoMeta";

export default function GalleryPage() {
  const [galleries, setGalleries] = useState([]);
  const [activeFilter, setActiveFilter] = useState("alle");
  const [isLoading, setIsLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(null); // For lightbox

  useEffect(() => {
    // Set metadata for Galleri (max 512px title, max 970px description)
    applyPageMeta(PAGE_SEO_CONFIG.gallery);

    // Fetch all galleries from CMS
    fetch("https://api.dev.auxo.dk/public/content/gallery")
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) {
          setGalleries(data);
        }
        setIsLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching galleries:", err);
        setIsLoading(false);
      });
  }, []);

  // Filter options based on slugs
  const filters = [
    { label: "Alle projekter", value: "alle" },
    { label: "Renovering", value: "galleri-renovering" },
    { label: "Vinduer & Døre", value: "galleri-udskiftning-af-vinduer-og-doere" },
    { label: "Nyt tag", value: "galleri-nyt-tag" },
    { label: "Solceller", value: "galleri-opsaetning-af-solceller" },
    { label: "Køkken", value: "galleri-koekkenrenovering" },
  ];

  // Get filtered images
  const getFilteredImages = () => {
    if (activeFilter === "alle") {
      return galleries.flatMap((g) =>
        g.images.map((img) => ({
          src: img,
          galleryTitle: g.title,
          slug: g.slug._ || g.slug,
        }))
      );
    }
    const matchedGallery = galleries.find((g) => (g.slug._ || g.slug) === activeFilter);
    if (matchedGallery) {
      return matchedGallery.images.map((img) => ({
        src: img,
        galleryTitle: matchedGallery.title,
        slug: matchedGallery.slug._ || matchedGallery.slug,
      }));
    }
    return [];
  };

  const filteredImages = getFilteredImages();

  return (
    <div className="bg-white text-neutral-900 min-h-screen py-12 lg:py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <span className="text-yellow-600 font-bold text-sm uppercase tracking-wider block mb-2">
            Vores Håndværk
          </span>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-neutral-900">
            Se vores udførte projekter
          </h1>
          <p className="mt-4 text-lg text-neutral-600">
            Hos{" "}
            <Link
              to="/"
              className="text-yellow-600 hover:text-yellow-700 font-bold underline underline-offset-2"
              title="Gå til forsiden"
            >
              JG Mesterbyg
            </Link>{" "}
            sætter vi en ære i det gode håndværk. Gå på opdagelse i vores galleri og lad dig inspirere til dit eget projekt. Har du allerede et projekt i tankerne, kan du altid indhente et{" "}
            <Link to="/fa-tilbud" className="text-yellow-600 hover:text-yellow-700 font-semibold hover:underline">
              uforpligtende tilbud
            </Link>
            .
          </p>
        </div>

        {/* Filter Controls */}
        <div className="flex flex-wrap justify-center gap-2 mb-12">
          {filters.map((f) => (
            <button
              key={f.value}
              onClick={() => setActiveFilter(f.value)}
              className={`px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 cursor-pointer ${
                activeFilter === f.value
                  ? "bg-yellow-500 text-neutral-950 shadow-md"
                  : "bg-neutral-100 text-neutral-600 hover:bg-neutral-200"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Gallery Grid */}
        {isLoading ? (
          <div className="min-h-[40vh] flex flex-col items-center justify-center gap-4">
            <i className="fa-solid fa-spinner animate-spin text-4xl text-yellow-500" />
            <p className="font-semibold text-neutral-600">Henter galleribilleder...</p>
          </div>
        ) : filteredImages.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {filteredImages.map((img, idx) => (
              <div
                key={idx}
                onClick={() => setActiveImage(img.src)}
                className="relative h-64 rounded-2xl overflow-hidden border border-neutral-200 shadow-sm cursor-pointer group"
              >
                <img
                  src={`https://assets.auxodev.dk/cdn-cgi/image/width=500,quality=80,format=auto/${img.src}`}
                  alt={getImageAlt(img.src, `${img.galleryTitle} reference`)}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex flex-col justify-between p-4">
                  <div className="self-end bg-yellow-500 text-neutral-950 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-md">
                    {img.galleryTitle.replace(" Galleri", "")}
                  </div>
                  <div className="flex items-center justify-between text-white">
                    <span className="text-xs font-semibold">Se billede</span>
                    <i className="fa-solid fa-magnifying-glass-plus text-lg text-yellow-500" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16 bg-neutral-50 rounded-2xl border border-neutral-200">
            <i className="fa-solid fa-images text-4xl text-neutral-300 mb-3" />
            <p className="text-neutral-500 font-semibold">Ingen billeder fundet i denne kategori.</p>
          </div>
        )}

        {/* CTA Section */}
        <div className="mt-16 bg-neutral-900 text-white p-8 md:p-12 rounded-3xl shadow-xl border border-neutral-800 text-center relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-yellow-500/10 rounded-bl-full pointer-events-none" />
          <h2 className="text-2xl sm:text-3xl font-extrabold mb-4">Inspireret til dit eget projekt?</h2>
          <p className="text-neutral-300 max-w-2xl mx-auto text-sm md:text-base mb-8">
            Lad os tage en uforpligtende snak om dine muligheder og ønsker. Vi udarbejder gerne et skræddersyet tilbud.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/fa-tilbud"
              className="bg-yellow-500 hover:bg-yellow-600 text-neutral-950 font-bold px-8 py-3.5 rounded-xl shadow-md transition-all duration-200 cursor-pointer"
            >
              Få et tilbud nu
            </Link>
            <Link
              to="/kontakt"
              className="bg-neutral-800 hover:bg-neutral-700 text-white font-bold px-8 py-3.5 rounded-xl border border-neutral-700 transition-all duration-200"
            >
              Kontakt os
            </Link>
          </div>
        </div>
      </div>

      {/* Lightbox Modal */}
      {activeImage && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4 animate-in fade-in duration-200"
          onClick={() => setActiveImage(null)}
        >
          <button
            className="absolute top-4 right-4 text-white text-3xl hover:text-yellow-500 cursor-pointer"
            onClick={() => setActiveImage(null)}
          >
            <i className="fa-solid fa-xmark" />
          </button>
          <div className="max-w-5xl max-h-[85vh] overflow-hidden rounded-xl border border-neutral-800 shadow-2xl">
            <img
              src={`https://assets.auxodev.dk/assets/${activeImage.split("/assets/")[1] || activeImage}`}
              alt={getImageAlt(activeImage, "JG Mesterbyg galleri reference")}
              className="max-w-full max-h-[85vh] object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </div>
  );
}
